import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { QrAPI, BuildingAPI } from "../../../services/api";
import QrViewModal from "./QrViewModal";
import { ReactComponent as MagnifyingGlassIcon } from "../../../assets/icons/dashboard/magnifyingglass.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/icons/listallbuilding/download.svg";

const PAGE_SIZE = 8;

export default function QrList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [buildingId, setBuildingId] = useState("");
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [viewing, setViewing] = useState(null); // qr object สำหรับ popup

  // โหลดรายชื่ออาคารใส่ dropdown
  useEffect(() => {
    BuildingAPI.list()
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.items || [];
        setBuildings(list);
      })
      .catch(() => setBuildings([]));
  }, []);

  // โหลด QR list
  const reload = () => {
    setLoading(true);
    setErrMsg("");
    QrAPI.list({
      page,
      pageSize: PAGE_SIZE,
      search,
      buildingId,
    })
      .then((data) => {
        if (Array.isArray(data)) {
          setItems(data);
          setTotal(data.length);
        } else {
          setItems(data?.items || []);
          setTotal(data?.total || 0);
        }
      })
      .catch((e) => {
        setItems([]);
        setTotal(0);
        setErrMsg(e.message || t("admin.qrList.loadFailed"));
      })
      .finally(() => setLoading(false));
  };

  // debounce search
  useEffect(() => {
    const t = setTimeout(reload, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, buildingId, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const onToggleStatus = async (qr) => {
    const next = qr.status === "active" ? "inactive" : "active";
    // optimistic update
    setItems((prev) =>
      prev.map((x) => (x.id === qr.id ? { ...x, status: next } : x))
    );
    try {
      await QrAPI.setStatus(qr.id, next);
    } catch (e) {
      // rollback
      setItems((prev) =>
        prev.map((x) => (x.id === qr.id ? { ...x, status: qr.status } : x))
      );
      alert(e.message || t("admin.qrList.statusChangeFailed"));
    }
  };

  const onExport = () => {
    // simple CSV export ของหน้านี้
    const header = ["code", "name", "x", "y", "buildingName", "floorName", "scanCount", "status", "createdAt"];
    const rows = items.map((q) =>
      header.map((k) => JSON.stringify(q[k] ?? "")).join(",")
    );
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-codes-page${page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const pageNumbers = useMemo(() => {
    // แสดง: 1 ... current-1, current, current+1 ... last
    const out = new Set([1, totalPages, page - 1, page, page + 1]);
    return [...out]
      .filter((n) => n >= 1 && n <= totalPages)
      .sort((a, b) => a - b);
  }, [page, totalPages]);

  return (
    <div className="p-1 min-h-[calc(100vh-96px)] flex flex-col gap-4">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-slate-900">
          {t("admin.qrList.title")}
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          {t("admin.qrList.subtitle")}
        </p>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2 min-w-[220px] sm:min-w-[320px] bg-white">
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-500" />
            <input
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              placeholder={t("admin.qrList.searchPlaceholder")}
              className="w-full outline-none font-base text-base placeholder:text-slate-500"
            />
          </div>

          <div className="ml-auto flex items-center gap-3 flex-wrap justify-end">
            <select
              value={buildingId}
              onChange={(e) => {
                setPage(1);
                setBuildingId(e.target.value);
              }}
              className="h-10 px-3 rounded-lg border border-slate-100 bg-white text-sm min-w-[200px]"
            >
              <option value="">{t("admin.qrList.allBuildings")}</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>

            <button
              onClick={onExport}
              className="h-10 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-base flex items-center gap-2"
            >
              <DownloadIcon className="w-4 h-4 -mt-1 text-white" />
              {t("admin.qrList.export")}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-3 px-2 font-semibold">{t("admin.qrList.qrIcon", "")}</th>
                <th className="py-3 px-2 font-semibold">{t("admin.qrList.qrCodeCol")}</th>
                <th className="py-3 px-2 font-semibold">{t("admin.qrList.qrNameCol")}</th>
                <th className="py-3 px-2 font-semibold">{t("admin.qrList.locationFloorCol")}</th>
                <th className="py-3 px-2 font-semibold">{t("admin.qrList.createdAtCol")}</th>
                <th className="py-3 px-2 font-semibold">{t("admin.qrList.statusCol")}</th>
                <th className="py-3 px-2 font-semibold text-center">{t("admin.qrList.onOffCol")}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-slate-400">
                    {t("common.loading")}
                  </td>
                </tr>
              )}

              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-slate-400">
                    {errMsg || t("admin.qrList.noQrData")}
                  </td>
                </tr>
              )}

              {!loading &&
                items.map((q) => (
                  <tr
                    key={q.id}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/admin/qr/${q.id}`)}
                  >
                    <td className="py-3 px-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewing(q);
                        }}
                        className="w-10 h-10 rounded-md border border-slate-700 bg-white flex items-center justify-center hover:bg-blue-50"
                        title={t("admin.qrList.viewQrCode")}
                      >
                        <img
                          src={QrAPI.imageUrl(q.id, 64, q.code)}
                          alt={q.code}
                          className="w-8 h-8"
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                      </button>
                    </td>
                    <td className="py-3 px-2 text-green-600">{q.code}</td>
                    <td className="py-3 px-2 text-slate-700">{q.name}</td>
                    <td className="py-3 px-2 text-slate-700">
                      X: {Number(q.x).toFixed(1)}  Y: {Number(q.y).toFixed(1)}
                    </td>
                    <td className="py-3 px-2 text-slate-700">
                      {formatDate(q.createdAt, i18n.language)}
                    </td>
                    <td className="py-3 px-2">
                      <StatusBadge status={q.status} />
                    </td>
                    <td className="py-3 px-2 text-center">
                      <Toggle
                        on={q.status === "active"}
                        onChange={(e) => {
                          e.stopPropagation();
                          onToggleStatus(q);
                        }}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-1">
            <PageBtn
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              ‹
            </PageBtn>
            {pageNumbers.map((n, i) => {
              const prev = pageNumbers[i - 1];
              return (
                <React.Fragment key={n}>
                  {prev && n - prev > 1 && (
                    <span className="px-2 text-slate-400">...</span>
                  )}
                  <PageBtn active={n === page} onClick={() => setPage(n)}>
                    {String(n).padStart(2, "0")}
                  </PageBtn>
                </React.Fragment>
              );
            })}
            <PageBtn
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              ›
            </PageBtn>
          </div>
        )}
      </div>

      {/* View modal */}
      {viewing && (
        <QrViewModal
          qr={viewing}
          onClose={() => setViewing(null)}
          onDeleted={() => {
            setViewing(null);
            reload();
          }}
        />
      )}
      {/* Footer */}
      <div className="mt-auto text-left border border-slate-200 rounded-lg p-4 bg-white text-xs text-slate-500">
        {t("admin.feedbackDetail.copyright")}
      </div>
    </div>
  );
}

function formatDate(s, lng) {
  if (!s) return "-";
  try {
    const locale = lng === "en" ? "en-US" : "th-TH";
    return new Date(s).toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return s;
  }
}

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const isOn = status === "active";
  return (
    <span
      className={`inline-flex items-center gap-1 px-4 py-1 rounded-lg text-xs font-semibold ${isOn
        ? "bg-emerald-50 text-emerald-700 border border-emerald-700"
        : "bg-slate-100 text-slate-500 border border-slate-200"
        }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${isOn ? "bg-emerald-700" : "bg-slate-400"
          }`}
      />
      {isOn ? t("admin.qrList.active") : t("admin.qrList.inactive")}
    </span>
  );
}

function Toggle({ on, onChange }) {
  return (
    <button
      onClick={onChange}
      className={`relative w-11 h-6 rounded-full transition ${on ? "bg-emerald-700" : "bg-slate-300"
        }`}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition ${on ? "left-[22px]" : "left-0.5"
          }`}
      />
    </button>
  );
}

function PageBtn({ children, active, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`min-w-8 h-8 px-2 rounded-md text-sm font-semibold ${active
        ? "bg-blue-600 text-white"
        : disabled
          ? "text-slate-300 cursor-not-allowed"
          : "text-slate-600 hover:bg-slate-100"
        }`}
    >
      {children}
    </button>
  );
}
